import type {
  Fee,
  Offer,
  OfferDuplicateDraft,
  OfferInput,
  PaymentLeg,
  PaymentLegInput,
  SettlementLeg,
  SettlementLegInput,
  TrafficType,
} from '@psp-aggregator/sdk';

import type { OfferLegKey } from '../model/legs';

export interface OfferCustomField {
  key: string;
  value: string;
}

export interface OfferRollingReserve {
  cap_amount: string;
  hold_days: string;
  reserve_pct: string;
}

export interface OfferLegDraft {
  asset: string;
  cost_fixed: string;
  cost_pct: string;
  enabled: boolean;
  limits: string;
  kyc_limits: string;
  methods: string[];
  min_amount: string;
  min_fee_fixed: string;
  min_fee_pct: string;
  provider: string;
  rate: string;
  sell_fixed: string;
  sell_pct: string;
  terms: string;
}

export interface OfferDraft {
  alias: string;
  base_currency: string;
  chargeback: string;
  countries: string[];
  currencies: string[];
  custom_fields: OfferCustomField[];
  details: string;
  flow: string;
  guarantee_amount: string;
  integration_methods: string[];
  legs: Record<OfferLegKey, OfferLegDraft>;
  name: string;
  provider_id: string;
  refund: string;
  regions: string[];
  requirements: string;
  restricted_countries: string[];
  rolling_reserve: OfferRollingReserve;
  traffic_type: string;
}

const SETTLEMENT_LEGS = new Set<OfferLegKey>(['crypto_topup', 'settlement']);

export const emptyLegDraft = (enabled = false): OfferLegDraft => ({
  asset: '',
  cost_fixed: '',
  cost_pct: enabled ? '0' : '',
  enabled,
  kyc_limits: '',
  limits: '',
  methods: [],
  min_amount: '',
  min_fee_fixed: '',
  min_fee_pct: '',
  provider: '',
  rate: '',
  sell_fixed: '',
  sell_pct: enabled ? '0' : '',
  terms: '',
});

const emptyRollingReserve = (): OfferRollingReserve => ({
  cap_amount: '',
  hold_days: '',
  reserve_pct: '',
});

export const emptyOfferDraft = (): OfferDraft => ({
  alias: '',
  base_currency: '',
  chargeback: '',
  countries: [],
  currencies: [],
  custom_fields: [],
  details: '',
  flow: '',
  guarantee_amount: '',
  integration_methods: [],
  legs: {
    crypto_topup: emptyLegDraft(),
    payin: emptyLegDraft(),
    payout: emptyLegDraft(),
    settlement: emptyLegDraft(),
  },
  name: '',
  provider_id: '',
  refund: '',
  regions: [],
  requirements: '',
  restricted_countries: [],
  rolling_reserve: emptyRollingReserve(),
  traffic_type: '',
});

const TRAFFIC_LABEL: Record<TrafficType, string> = {
  ftd: 'FTD',
  grey: 'Grey',
  incentive: 'Incentive',
  nftd: 'NFTD',
  quasi_ecom: 'Quasi-ecom',
  trusted: 'Trusted',
};

const TRAFFIC_BY_LABEL: Record<string, TrafficType> = {
  FTD: 'ftd',
  ftd: 'ftd',
  Grey: 'grey',
  grey: 'grey',
  Incentive: 'incentive',
  incentive: 'incentive',
  NFTD: 'nftd',
  nftd: 'nftd',
  'Quasi-ecom': 'quasi_ecom',
  quasi_ecom: 'quasi_ecom',
  Trusted: 'trusted',
  trusted: 'trusted',
};

const feeFromDraft = (percent: string, fixed: string): Fee => {
  if (fixed.trim()) {
    return { unit: 'fixed', value: fixed.trim() };
  }

  return { unit: 'percent', value: percent.trim() || '0' };
};

const feeToDraft = (
  fee: Fee | undefined,
): { fixed: string; percent: string } => {
  if (!fee) {
    return { fixed: '', percent: '' };
  }

  if (fee.unit === 'fixed') {
    return { fixed: fee.value, percent: '' };
  }

  return { fixed: '', percent: fee.value };
};

const limitsText = (
  limits: { max?: string; min?: string } | undefined,
): string => [limits?.min, limits?.max].filter(Boolean).join('-');

const parseLimits = (
  value: string,
): { max?: string; min?: string } | undefined => {
  const [min, max] = value.split('-').map((part) => part.trim());

  if (!min && !max) {
    return undefined;
  }

  return {
    max: max || undefined,
    min: min || undefined,
  };
};

const readPaymentLeg = (leg: PaymentLeg | undefined): OfferLegDraft => {
  const cost = feeToDraft(leg?.provider_rate);
  const sell = feeToDraft(leg?.sell_rate);

  return {
    ...emptyLegDraft(),
    cost_fixed: cost.fixed,
    cost_pct: cost.percent,
    enabled: Boolean(leg),
    kyc_limits: leg?.kyc_limits ?? '',
    limits: limitsText(leg?.limits),
    methods: leg?.methods ?? [],
    min_fee_fixed: feeToDraft(leg?.min_fee).fixed,
    min_fee_pct: feeToDraft(leg?.min_fee).percent,
    sell_fixed: sell.fixed,
    sell_pct: sell.percent,
  };
};

const readSettlementLeg = (leg: SettlementLeg | undefined): OfferLegDraft => ({
  ...readPaymentLeg(leg),
  asset: leg?.asset ?? '',
  min_amount: leg?.min_amount ?? '',
  provider: leg?.provider ?? '',
  rate: leg?.exchange_rate ?? '',
  terms: leg?.terms ?? '',
});

const paymentInput = (value: OfferLegDraft): PaymentLegInput => ({
  kyc_limits: value.kyc_limits.trim() || undefined,
  limits: parseLimits(value.limits),
  methods: value.methods,
  min_fee:
    value.min_fee_pct.trim() || value.min_fee_fixed.trim()
      ? feeFromDraft(value.min_fee_pct, value.min_fee_fixed)
      : undefined,
  provider_rate: feeFromDraft(value.cost_pct, value.cost_fixed),
  sell_rate: feeFromDraft(value.sell_pct, value.sell_fixed),
});

const settlementInput = (value: OfferLegDraft): SettlementLegInput => ({
  ...paymentInput(value),
  asset: value.asset.trim() || undefined,
  exchange_rate: value.rate.trim() || '1',
  limits: parseLimits(value.limits),
  min_amount: value.min_amount.trim() || undefined,
  provider: value.provider.trim() || undefined,
  terms: value.terms.trim() || undefined,
});

const trafficTypes = (value: string): TrafficType[] => {
  const types = value
    .split(',')
    .map((item) => TRAFFIC_BY_LABEL[item.trim()])
    .filter(Boolean);

  return [...new Set(types)];
};

export const offerToDraft = (
  offer: Offer | OfferDuplicateDraft,
): OfferDraft => ({
  alias: offer.alias,
  base_currency: offer.currencies.length === 1 ? offer.currencies[0] : '',
  chargeback: offer.limits_and_fees?.chargeback ?? '',
  countries: offer.countries.map((country) => country.toUpperCase()),
  currencies: offer.currencies.map((currency) => currency.toUpperCase()),
  custom_fields: (offer.additional?.custom_fields ?? []).map((field) => ({
    key: field.name,
    value: field.value,
  })),
  details: offer.additional?.notes ?? '',
  flow: offer.additional?.flow ?? '',
  guarantee_amount: offer.limits_and_fees?.guarantee_amount ?? '',
  integration_methods: offer.integration_methods,
  legs: {
    crypto_topup: readSettlementLeg(offer.crypto_top_up),
    payin: readPaymentLeg(offer.pay_in),
    payout: readPaymentLeg(offer.pay_out),
    settlement: readSettlementLeg(offer.settlement),
  },
  name: offer.name,
  provider_id: offer.provider_id ?? '',
  refund: offer.limits_and_fees?.refund ?? '',
  regions: [],
  requirements: offer.additional?.requirements ?? '',
  restricted_countries: [],
  rolling_reserve: {
    cap_amount: offer.limits_and_fees?.rolling_reserve?.cap_limit ?? '',
    hold_days:
      offer.limits_and_fees?.rolling_reserve?.hold_days != null
        ? String(offer.limits_and_fees.rolling_reserve.hold_days)
        : '',
    reserve_pct: offer.limits_and_fees?.rolling_reserve?.percent ?? '',
  },
  traffic_type: offer.traffic_types
    .map((type) => TRAFFIC_LABEL[type])
    .join(', '),
});

export const duplicateToDraft = (draft: OfferDuplicateDraft): OfferDraft =>
  offerToDraft(draft);

export const draftToOfferInput = (draft: OfferDraft): OfferInput => {
  const holdRaw = draft.rolling_reserve.hold_days.trim();
  const holdDays = Number(holdRaw);
  const fields = draft.custom_fields.filter(
    (field) => field.key || field.value,
  );

  return {
    additional: {
      custom_fields:
        fields.length > 0
          ? fields.map((field) => ({ name: field.key, value: field.value }))
          : undefined,
      flow: draft.flow.trim() || undefined,
      notes: draft.details.trim() || undefined,
      requirements: draft.requirements.trim() || undefined,
    },
    alias: draft.alias.trim(),
    countries: draft.countries,
    crypto_top_up: draft.legs.crypto_topup.enabled
      ? settlementInput(draft.legs.crypto_topup)
      : undefined,
    currencies: draft.currencies,
    integration_methods: draft.integration_methods,
    limits_and_fees: {
      chargeback: draft.chargeback.trim() || undefined,
      guarantee_amount: draft.guarantee_amount.trim() || undefined,
      refund: draft.refund.trim() || undefined,
      rolling_reserve: {
        cap_limit: draft.rolling_reserve.cap_amount.trim() || undefined,
        hold_days: holdRaw && Number.isFinite(holdDays) ? holdDays : undefined,
        percent: draft.rolling_reserve.reserve_pct.trim() || undefined,
      },
    },
    name: draft.name.trim() || undefined,
    pay_in: draft.legs.payin.enabled
      ? paymentInput(draft.legs.payin)
      : undefined,
    pay_out: draft.legs.payout.enabled
      ? paymentInput(draft.legs.payout)
      : undefined,
    provider_id: draft.provider_id,
    settlement: draft.legs.settlement.enabled
      ? settlementInput(draft.legs.settlement)
      : undefined,
    traffic_types: trafficTypes(draft.traffic_type),
  };
};

export type OfferUpdateInput = Omit<
  OfferInput,
  'crypto_top_up' | 'pay_in' | 'pay_out' | 'settlement'
> & {
  crypto_top_up?: SettlementLegInput | null;
  pay_in?: PaymentLegInput | null;
  pay_out?: PaymentLegInput | null;
  settlement?: SettlementLegInput | null;
};

export const draftToOfferUpdate = (draft: OfferDraft): OfferUpdateInput => {
  const input = draftToOfferInput(draft);

  return {
    ...input,
    crypto_top_up: draft.legs.crypto_topup.enabled ? input.crypto_top_up : null,
    pay_in: draft.legs.payin.enabled ? input.pay_in : null,
    pay_out: draft.legs.payout.enabled ? input.pay_out : null,
    settlement: draft.legs.settlement.enabled ? input.settlement : null,
  };
};

export const legMethodLabel = (
  leg: OfferLegKey,
  value: OfferLegDraft,
): string =>
  SETTLEMENT_LEGS.has(leg)
    ? value.terms || value.methods.join(', ')
    : value.methods.join(', ');
